// Login screen (ADR 0012 + ADR 0021 + ADR 0034).
//
// Mode selected at build time via VITE_AUTH_MODE:
//   - 'dev'      (default): email → POST /v1/dev/login → JWT.
//   - 'supabase': email → supabase.auth.signInWithOtp → magic link.
//     The link lands on /auth/callback where the token is stashed.
//
// The dev login screen stays mounted even in a supabase build: a
// production API refuses to mount /v1/dev/login, so a mismatched
// deploy fails on the next click, not silently.

import { useState, type FormEvent, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { getAuthMode, getSupabaseClient } from '../auth/supabase-client.js';
import { ApiError } from '../api/client.js';

export function LoginPage(): JSX.Element {
  const { token, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const mode = getAuthMode();

  if (token) return <Navigate to="/home" replace />;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    const normalised = email.trim().toLowerCase();
    try {
      if (mode === 'supabase') {
        const client = getSupabaseClient();
        const callbackUrl = `${globalThis.location.origin}/auth/callback`;
        const { error: supaErr } = await client.auth.signInWithOtp({
          email: normalised,
          options: { emailRedirectTo: callbackUrl },
        });
        if (supaErr) throw supaErr;
        setInfo(
          `A sign-in link has been sent to ${normalised}. Open it on this device to continue.`,
        );
      } else {
        await login(normalised);
        navigate('/home', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        setError('Unknown email. Only seeded dev users can sign in.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else if (mode === 'supabase' && err instanceof Error) {
        // Supabase client errors carry a useful human-readable message
        // (rate-limit, bad email) that the user can act on.
        setError(err.message);
      } else {
        setError('Could not reach the API.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const subtitle =
    mode === 'supabase'
      ? 'Owner dashboard · sign in with a magic link'
      : 'Owner dashboard · dev-auth sign-in';
  const submitLabel =
    mode === 'supabase'
      ? submitting
        ? 'Sending link…'
        : 'Send magic link'
      : submitting
        ? 'Signing in…'
        : 'Sign in';

  return (
    <main className="auth-wrap">
      <section className="card auth-card">
        <h1>Tabletop</h1>
        <p className="muted">{subtitle}</p>
        <form onSubmit={onSubmit} noValidate>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
          {error ? (
            <p role="alert" className="error">
              {error}
            </p>
          ) : null}
          {info ? <p className="muted">{info}</p> : null}
          <button type="submit" disabled={submitting || email.trim() === ''}>
            {submitLabel}
          </button>
        </form>
      </section>
    </main>
  );
}
